import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Modal, Header, Button, Message, Checkbox } from 'semantic-ui-react';
import { v4 as uuidv4 } from 'uuid';
import { stringToHexColor } from 'categories/Components/ColorPicker';

const BulkAddOptions = ({
    open,
    setOpen,
    optionItem,
    existingOptions,
    setSurveyOptions,
    surveyStrucRow,
    setSurveyStrucRow,
    setUpdateJsonFile,
}) => {
    const [pasteText, setPasteText] = useState('');
    const [errorText, setErrorText] = useState('');
    const [disableFrench, setDisableFrench] = useState(false);

    const parsed = useMemo(() => {
        if (!pasteText || pasteText.trim().length === 0) return [];
        const lines = pasteText
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter((l) => l.length > 0);
        const items = [];
        for (const line of lines) {
            if (disableFrench) {
                const name = line.trim();
                if (name.length === 0) continue;
                items.push({ name, fr: '' });
            } else {
                const parts = line.split(/\t|,/);
                const name = (parts[0] || '').trim();
                const fr = (parts[1] || '').trim();
                if (name.length === 0) continue;
                items.push({ name, fr });
            }
        }
        return items;
    }, [pasteText, disableFrench]);

    function buildOptionObjects(items) {
        return items.map((it) => ({
            id: uuidv4(),
            name: it.name,
            fr: it.fr || '',
            description: '',
            color: stringToHexColor(it.name, 50, 60),
        }));
    }

    function updateParentAndClose(newList) {
        // Update local options UI
        setSurveyOptions(newList);
        // Update parent category list
        const ExcludedList = surveyStrucRow.filter((sc) => sc.id !== optionItem.id);
        const UpdatedItem = { ...optionItem, options: newList };
        ExcludedList.push(UpdatedItem);
        setSurveyStrucRow(ExcludedList);
        setUpdateJsonFile(Math.random());
        setOpen(false);
    }

    function appendOptions() {
        setErrorText('');
        if (parsed.length === 0) {
            setErrorText('Nothing to add. Paste at least one line.');
            return;
        }
        const newItems = buildOptionObjects(parsed);
        const base = Array.isArray(existingOptions) ? existingOptions : [];
        updateParentAndClose([...base, ...newItems]);
    }

    function replaceOptions() {
        setErrorText('');
        if (parsed.length === 0) {
            setErrorText('Nothing to replace with. Paste at least one line.');
            return;
        }
        const newItems = buildOptionObjects(parsed);
        updateParentAndClose(newItems);
    }

    function onClose() {
        setOpen(false);
    }

    return (
        <Modal open={open} onClose={onClose} size="large">
            <Modal.Header>
                <Header as="h1">
                    Bulk Add Options
                    <Header.Subheader>
                        Paste rows as either "English" or "English, French" (CSV) or
                        "English&lt;TAB&gt;French" (TSV).
                    </Header.Subheader>
                </Header>
            </Modal.Header>
            <Modal.Content scrolling>
                <Modal.Description>
                    {errorText && (
                        <Message negative header="Cannot proceed" content={errorText} />
                    )}

                    <div>
                        <Checkbox
                            toggle
                            label="Disable French column (treat entire line as English)"
                            checked={disableFrench}
                            onChange={(_, data) => setDisableFrench(Boolean(data.checked))}
                        />
                    </div>

                    <Label>Paste English[, French] rows</Label>
                    <TextArea
                        placeholder={"e.g.\nMale, Homme\nFemale, Femme\nNon-binary\nPrefer not to say"}
                        value={pasteText}
                        onChange={(e) => setPasteText(e.target.value)}
                    />

                    <Stats>
                        Parsed lines: <b>{parsed.length}</b>
                        {parsed.length > 0 && (
                            <Preview>
                                Preview first 5:
                                {parsed.slice(0, 5).map((p, idx) => (
                                    <div key={idx}>
                                        {p.name} {p.fr ? `→ ${p.fr}` : ''}
                                    </div>
                                ))}
                            </Preview>
                        )}
                    </Stats>
                </Modal.Description>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="grey">
                    Cancel
                </Button>
                <Button onClick={replaceOptions} color="red" disabled={parsed.length === 0}>
                    Replace All
                </Button>
                <Button onClick={appendOptions} color="violet" disabled={parsed.length === 0}>
                    Append
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default BulkAddOptions;

const Label = styled.div`
    margin: 10px 0 5px 0;
    font-weight: bold;
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: 220px;
    font-family: monospace;
    padding: 10px;
`;

const Stats = styled.div`
    margin-top: 10px;
`;

const Preview = styled.div`
    margin-top: 6px;
`;


